/**
 * Sprint 23.5 — HireWizardPage (`/hire`).
 *
 * The conversational composition surface. Split-screen overlay:
 *  - LEFT  → HireConversation (Sprint 22 chat infra, scope=composition)
 *  - RIGHT → HireDraftPanel (live form synced via tool-call interception)
 *
 * Lifecycle:
 *   mount   → fresh conversationId + empty draft
 *   stream  → AI emits update_composition_draft tool calls → patches draft
 *   hire    → create_agent → update_node (persona) → delete_chat_session →
 *             navigate("/workforce")
 *   cancel  → delete_chat_session → navigate back
 *
 * Theme-aware throughout (title, greeting, button labels).
 *
 * Per Q15 the surface is a full route, but visually a modal-style overlay
 * sized for split-screen comfort at desktop widths. The AppShell layout
 * continues to render underneath; ChatRail returns null on this route so
 * the right column collapses cleanly.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/hooks/use-auth";
import { useListTree, useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { useConnections, useEquipAgent } from "../../hooks/use-connections";
import { mcpClient } from "../../lib/mcp-client";
import { showToast } from "../components/primitives";
import { useThemedLabels } from "../hooks/use-themed-labels";
import { useCompositionChatRuntime } from "../hooks/use-composition-chat-runtime";
import { HireConversation } from "../components/hire-wizard/HireConversation";
import { HireDraftPanel } from "../components/hire-wizard/HireDraftPanel";
import { HireWizardEntry } from "../components/hire-wizard/HireWizardEntry";
import { HireWizardTemplateGallery } from "../components/hire-wizard/HireWizardTemplateGallery";
import { HireWizardTemplatePreview } from "../components/hire-wizard/HireWizardTemplatePreview";
import { HireWizardTemplateCustomise } from "../components/hire-wizard/HireWizardTemplateCustomise";
import { WizardProgressStrip } from "../components/hire-wizard/template-visuals";
import type { HireFromTemplateResult } from "../hooks/use-hire-from-template";
import {
  applyDraftPatch,
  canHire,
  emptyHireDraft,
  type HireDraft,
  type HirePersonaStyle,
} from "../components/hire-wizard/types";
import { queryKeys } from "../../lib/query-keys";

/** The hire wizard's step machine (Sprint 24.15 fork). */
type WizardStep = "entry" | "scratch" | "gallery" | "preview" | "customise";

// Per-field regenerate prompts the sparkle queues as a focused user turn. The
// system prompt already enforces silent tool calls + short conversational
// replies, so these stay terse — just the field + the direction to take it.
const REGENERATE_PROMPTS: Record<keyof HireDraft, string> = {
  name: "Try a different name.",
  role: "Tighten the role.",
  bio: "Regenerate the bio — warmer this time.",
  style: "Pick the style that best fits.",
  voice_tone: "Try a different voice / tone.",
  motivations: "Rework the motivations.",
  specialties: "Suggest different specialties.",
  reports_to: "Pick a better parent if one fits.",
  equipped_with: "Recommend a tight set of connections.",
};

export function HireWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const connectionsQ = useConnections();
  const equipAgent = useEquipAgent();
  const { currentWorkspaceId } = useAuth();
  const labels = useThemedLabels();
  const workspace = useWorkspaceInfo();
  const tree = useListTree(currentWorkspaceId ?? undefined);

  const rootNodeId = (workspace.data?.root_node_id as string | undefined) ?? null;
  const workspaceName = workspace.data?.workspace?.name ?? "Workspace";
  const allNodes = tree.data ?? [];

  const [searchParams] = useSearchParams();
  const initialParentId = searchParams.get("parent_id");

  const [conversationId] = useState<string>(() => crypto.randomUUID());
  const [draft, setDraft] = useState<HireDraft>(() => {
    // Sprint 23.7 — onboarding's "Refine first" pre-fills the draft via a
    // `?suggested=<json>` param (the suggest_first_hire shape). Parse defensively.
    const d = emptyHireDraft();
    if (initialParentId) d.reports_to = initialParentId;
    const suggestedRaw = searchParams.get("suggested");
    if (suggestedRaw) {
      try {
        const s = JSON.parse(suggestedRaw) as Record<string, unknown>;
        if (typeof s.name === "string") d.name = s.name;
        if (typeof s.role === "string") d.role = s.role;
        if (typeof s.bio === "string") d.bio = s.bio;
        if (typeof s.motivations === "string") d.motivations = s.motivations;
        const styles: HirePersonaStyle[] = ["matter-of-fact", "conversational", "playful", "formal"];
        if (typeof s.style === "string" && (styles as string[]).includes(s.style)) {
          d.style = s.style as HirePersonaStyle;
        }
        if (Array.isArray(s.specialties)) {
          d.specialties = s.specialties.filter((x): x is string => typeof x === "string");
        }
        if (Array.isArray(s.equipped_with)) {
          d.equipped_with = s.equipped_with.filter((x): x is string => typeof x === "string");
        }
      } catch {
        /* ignore malformed suggested param */
      }
    }
    return d;
  });
  const [busy, setBusy] = useState(false);

  // Sprint 24.15 — the wizard now forks at step 1. Deep-links preserve the prior
  // flows: `?prefill`/`?suggested` (onboarding "refine") jump straight to the
  // scratch composition; `?start=gallery` (workforce/welcome "Browse templates")
  // jumps straight to the template gallery. Everything else opens the fork.
  const [step, setStep] = useState<WizardStep>(() => {
    if (searchParams.get("prefill") || searchParams.get("suggested")) return "scratch";
    // Sprint 24.15.2 — `?start=customise&template=<id>` re-opens the customise
    // step after a Connections round-trip (the equipment-missing "Open
    // Connections" return path). The draft is restored from sessionStorage.
    if (searchParams.get("start") === "customise" && searchParams.get("template")) return "customise";
    if (searchParams.get("start") === "gallery") return "gallery";
    return "entry";
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    () => (searchParams.get("start") === "customise" ? searchParams.get("template") : null),
  );

  // Hold a ref so the chat runtime's onDraftPatch callback always sees the
  // latest setDraft (it's stable, but the closure capture is reused).
  const onDraftPatch = useCallback((field: string, value: unknown) => {
    setDraft((prev) => applyDraftPatch(prev, field, value));
  }, []);

  const { runtime, chatMessages, status, error } = useCompositionChatRuntime({
    conversationId,
    workspaceId: currentWorkspaceId ?? undefined,
    draft,
    onDraftPatch,
  });

  const greeting = useMemo(() => labels.wizardGreeting, [labels]);

  // Sprint 23.7 — `?prefill=<intent>` (from onboarding / empty-state starters)
  // seeds the conversation: once the runtime is ready, send the intent as the
  // first user message so the AI starts composing from it.
  const prefillSentRef = useRef(false);
  useEffect(() => {
    const prefill = searchParams.get("prefill");
    if (!prefill || prefillSentRef.current || status !== "ready") return;
    prefillSentRef.current = true;
    try {
      runtime.thread.append({ role: "user", content: [{ type: "text", text: prefill }] });
    } catch {
      prefillSentRef.current = false; // retry on a later ready tick
    }
  }, [searchParams, status, runtime]);

  // Page-level "escape closes" — wizard is modal-style.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) {
        e.preventDefault();
        void handleCancel();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  const purgeSession = useCallback(async () => {
    try {
      await mcpClient.toolsCall("delete_chat_session", { session_id: conversationId });
    } catch {
      /* Best-effort — the row may not exist yet if no message went through. */
    }
  }, [conversationId]);

  const handleCancel = useCallback(async () => {
    await purgeSession();
    navigate("/workforce");
  }, [purgeSession, navigate]);

  const updateField = useCallback(
    <K extends keyof HireDraft>(field: K, value: HireDraft[K]) => {
      setDraft((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Regenerate a field — queue a focused turn the AI will answer with
  // exactly one update_composition_draft tool call for that field.
  const regenerateField = useCallback(
    (field: keyof HireDraft) => {
      const ask = REGENERATE_PROMPTS[field] ?? `Regenerate the ${String(field)} field.`;
      try {
        runtime.thread.append({
          role: "user",
          content: [{ type: "text", text: ask }],
        });
      } catch {
        /* If the thread isn't ready yet, silently skip — the field still has
         * its pencil-edit path. */
      }
    },
    [runtime],
  );

  const handleHire = useCallback(async () => {
    if (!canHire(draft) || busy) return;
    setBusy(true);
    try {
      // 1. Create the agent (basic fields).
      const created = await mcpClient.createAgent({
        name: draft.name.trim(),
        description: draft.role.trim() || undefined,
        parent_id: draft.reports_to ?? undefined,
      });

      // 2. Stamp the persona depth + connection scope via update_node.
      const newConfig: Record<string, unknown> = {
        ...(created.agent_config ?? {}),
        bio: draft.bio.trim() ? draft.bio.trim() : null,
        style: draft.style,
        voice_tone: draft.voice_tone.trim() ? draft.voice_tone.trim() : null,
        motivations: draft.motivations.trim() ? draft.motivations.trim() : null,
        specialties: draft.specialties.length > 0 ? draft.specialties : null,
      };

      try {
        await mcpClient.updateNode({
          id: created.id,
          agent_config: newConfig as unknown as never,
        });
      } catch {
        // Soft-fail: the agent exists; user can backfill persona later.
        showToast(
          `${draft.name.trim()} ${labels.hireConfirmAction.toLowerCase()}d, but couldn't save persona — retry from the agent profile.`,
          "error",
        );
      }

      // 2b. Sprint 24 — equip the selected MCP connections via the junction table
      // (connection_scope was dropped from agent_config). "all" expands to every
      // current MCP connection; otherwise the chosen ids.
      const equipIds =
        draft.equipped_with === "all"
          ? (connectionsQ.data ?? []).map((c) => c.connection_id)
          : draft.equipped_with;
      if (equipIds.length > 0) {
        try {
          await Promise.all(
            equipIds.map((connection_id) =>
              equipAgent.mutateAsync({
                node_id: created.id,
                connection_kind: "mcp",
                connection_id,
              }),
            ),
          );
        } catch {
          // Soft-fail: agent exists; equipment can be set from Settings → Access.
        }
      }

      // 3. Purge the composition chat session (transactional, no history).
      await purgeSession();

      // 4. Refresh caches and navigate to Workforce.
      queryClient.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      queryClient.invalidateQueries({ queryKey: ["workforce-status"] });
      navigate("/workforce");
    } catch (err) {
      setBusy(false);
      const msg =
        err instanceof Error ? err.message : `Couldn't ${labels.hireConfirmAction.toLowerCase()} that agent.`;
      showToast(msg, "error");
    }
  }, [draft, busy, purgeSession, queryClient, navigate, labels]);

  // Sprint 24.15 — shared post-hire tail for the template path (mirrors the
  // scratch handleHire tail): refresh caches + land on Workforce.
  const afterTemplateHire = useCallback(
    (result: HireFromTemplateResult) => {
      void purgeSession();
      queryClient.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      queryClient.invalidateQueries({ queryKey: ["workforce-status"] });
      showToast(`${result.name} ${labels.hireConfirmAction.toLowerCase()}d.`, "success");
      navigate("/workforce");
    },
    [purgeSession, queryClient, navigate, labels],
  );

  const progressStep: 1 | 2 | 3 = step === "entry" ? 1 : step === "customise" ? 3 : 2;

  return (
    <div style={backdrop} role="dialog" aria-label={labels.wizardTitle} aria-modal="true">
      <div style={shell}>
        <header style={topBar}>
          <div style={titleRow}>
            <i
              className="ti ti-user-plus"
              style={{ fontSize: 16, color: "var(--color-text-tertiary)" }}
              aria-hidden="true"
            />
            <span style={topTitle}>{labels.wizardTitle}</span>
          </div>
          <button
            type="button"
            onClick={() => void handleCancel()}
            style={closeBtn}
            disabled={busy}
            aria-label="Close"
            title="Close (Esc)"
          >
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </header>
        <WizardProgressStrip step={progressStep} />

        {step === "scratch" ? (
          <div style={split}>
            <div style={leftCol}>
              <HireConversation
                title={labels.wizardTitle}
                runtime={runtime}
                chatMessages={chatMessages}
                status={status}
                error={error}
                greeting={greeting}
              />
            </div>
            <div style={rightCol}>
              <HireDraftPanel
                draft={draft}
                onChange={updateField}
                onRegenerateField={regenerateField}
                onCancel={() => void handleCancel()}
                onHire={() => void handleHire()}
                busy={busy}
                hireLabel={labels.hireConfirmAction}
                rootNodeId={rootNodeId}
                workspaceName={workspaceName}
                allNodes={allNodes}
              />
            </div>
          </div>
        ) : step === "entry" ? (
          <HireWizardEntry
            onPickTemplate={() => setStep("gallery")}
            onPickScratch={() => setStep("scratch")}
          />
        ) : step === "gallery" ? (
          <HireWizardTemplateGallery
            onPreview={(id) => {
              setSelectedTemplateId(id);
              setStep("preview");
            }}
            onHire={(id) => {
              setSelectedTemplateId(id);
              setStep("customise");
            }}
          />
        ) : step === "preview" && selectedTemplateId ? (
          <HireWizardTemplatePreview
            templateId={selectedTemplateId}
            onCustomise={(id) => {
              setSelectedTemplateId(id);
              setStep("customise");
            }}
            onBack={() => setStep("gallery")}
          />
        ) : step === "customise" && selectedTemplateId ? (
          <HireWizardTemplateCustomise
            templateId={selectedTemplateId}
            onBack={() => setStep("preview")}
            onHired={afterTemplateHire}
            onCancelToGallery={() => {
              setSelectedTemplateId(null);
              setStep("gallery");
            }}
          />
        ) : (
          <HireWizardEntry
            onPickTemplate={() => setStep("gallery")}
            onPickScratch={() => setStep("scratch")}
          />
        )}
      </div>
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
};

const shell: CSSProperties = {
  width: 960,
  maxWidth: "94vw",
  height: 640,
  maxHeight: "92vh",
  display: "flex",
  flexDirection: "column",
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "0 24px 64px rgba(0, 0, 0, 0.32)",
  overflow: "hidden",
};

const topBar: CSSProperties = {
  flex: "0 0 auto",
  height: 48,
  display: "flex",
  alignItems: "center",
  padding: "0 12px 0 18px",
  borderBottom: "var(--kuvio-border-default)",
};

const titleRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flex: 1,
  minWidth: 0,
};

const topTitle: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const closeBtn: CSSProperties = {
  width: 32,
  height: 32,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const split: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "grid",
  gridTemplateColumns: "1fr 340px",
};

const leftCol: CSSProperties = {
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  overflow: "hidden",
};

const rightCol: CSSProperties = {
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  overflow: "hidden",
  background: "var(--color-background-primary)",
};
